import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Upload,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  DownloadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import DownloadableDocumentService from "../../../services/DownloadableDocumentService";

const { TextArea } = Input;
const { Option } = Select;

// Fixed category for village report documents
const FIXED_CATEGORY = "report";

const VillageReportDocuments = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await DownloadableDocumentService.getDocuments();
      // Filter only report documents
      const reportDocs = (res.data || []).filter(
        (d) => d.category === FIXED_CATEGORY
      );
      setList(reportDocs);
    } catch (err) {
      console.error(err);
      message.error("Failed to load village report documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setSelectedFile(null);
    setFileList([]);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      language: record.language || "Marathi",
    });
    setSelectedFile(null);
    setFileList([]);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Document",
      content: "Are you sure you want to delete this document?",
      async onOk() {
        try {
          await DownloadableDocumentService.deleteDocument(id);
          message.success("Deleted successfully");
          fetch();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const handleDownload = async (record) => {
    try {
      const response = await DownloadableDocumentService.downloadDocument(
        record.id
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${record.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Download started");
    } catch (err) {
      console.error(err);
      message.error("Download failed");
    }
  };

  const beforeUpload = (file) => {
    const isPDF = file.type === "application/pdf";
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!isPDF) {
      message.error("Only PDF files are allowed");
      return false;
    }
    if (file.size > maxSize) {
      message.error("File must be smaller than 10MB");
      return false;
    }

    setSelectedFile(file);
    setFileList([file]);
    return false;
  };

  const onFinish = async (values) => {
    if (!selectedFile && !editing) {
      message.error("Please select a PDF file");
      return;
    }

    const payload = {
      title: values.title,
      description: values.description || "",
      category: FIXED_CATEGORY, // Always use fixed category
      language: values.language || "Marathi",
    };

    setLoading(true);
    try {
      if (editing) {
        await DownloadableDocumentService.updateDocument(
          editing.id,
          payload,
          selectedFile
        );
        message.success("Updated successfully");
      } else {
        await DownloadableDocumentService.createDocument(payload, selectedFile);
        message.success("Created successfully");
      }
      setIsModalOpen(false);
      form.resetFields();
      setSelectedFile(null);
      setFileList([]);
      fetch();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => (
        <Space>
          <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
          <span style={{ maxWidth: 250, whiteSpace: "normal" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (t) => (
        <div
          style={{
            maxWidth: 300,
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t || "-"}
        </div>
      ),
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      render: (l) => l || "-",
    },
    {
      title: "File",
      key: "file",
      render: (_, record) => {
        const pdfUrl = record.file || record.pdf_file;
        return pdfUrl ? (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <Button size="small" icon={<FilePdfOutlined />}>
              View
            </Button>
          </a>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            type="primary"
            onClick={() => handleDownload(record)}
          >
            Download
          </Button>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>मुख्यमंत्री समृद्ध पंचायत राज अभियान</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Document
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editing ? "Edit Document" : "Upload New Document"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setSelectedFile(null);
          setFileList([]);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Enter document title" }]}
          >
            <Input placeholder="Enter document title" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Enter document description" />
          </Form.Item>

          {/* Category is fixed - show as disabled info */}
          <Form.Item label="Category">
            <Tag color="green" style={{ padding: "4px 12px", fontSize: 14 }}>
              मुख्यमंत्री समृद्ध पंचायत राज अभियान
            </Tag>
            <span style={{ marginLeft: 8, color: "#888", fontSize: 12 }}>
              Fixed category
            </span>
          </Form.Item>

          <Form.Item label="PDF File" required>
            <Upload
              beforeUpload={beforeUpload}
              fileList={fileList}
              showUploadList={true}
              accept=".pdf"
              maxCount={1}
              onRemove={() => {
                setSelectedFile(null);
                setFileList([]);
              }}
            >
              <Button icon={<UploadOutlined />}>Choose PDF File</Button>
            </Upload>
            {selectedFile && (
              <div style={{ marginTop: 8, color: "#52c41a" }}>
                Selected: {selectedFile.name}
              </div>
            )}
            {editing && !selectedFile && (
              <div style={{ marginTop: 8, color: "#faad14" }}>
                No new file selected. Current file will be retained.
              </div>
            )}
          </Form.Item>

          <Form.Item name="language" label="Language" initialValue="Marathi">
            <Select allowClear>
              <Option value="English">English</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Hindi">Hindi</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update Document" : "Upload Document"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Document Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(viewing)}
          >
            Download
          </Button>,
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {viewing && (
          <div>
            <div
              style={{ background: "#f5f5f5", padding: 16, borderRadius: 8 }}
            >
              <p>
                <strong>Title:</strong> {viewing.title}
              </p>
              <p>
                <strong>Description:</strong> {viewing.description || "-"}
              </p>
              <p>
                <strong>Category:</strong>{" "}
                <Tag color="green">मुख्यमंत्री समृद्ध पंचायत राज अभियान</Tag>
              </p>
              <p>
                <strong>Language:</strong> {viewing.language || "-"}
              </p>
              {(viewing.file || viewing.pdf_file) && (
                <p>
                  <strong>File:</strong>{" "}
                  <a
                    href={viewing.file || viewing.pdf_file}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="small" icon={<FilePdfOutlined />}>
                      View PDF
                    </Button>
                  </a>
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VillageReportDocuments;
